const CART_DRAWER_SELECTOR = '[data-drawer-id="cart-drawer"]';

/**
 * Tablist pattern for Cart / Wishlist panels inside the cart drawer.
 * Uses delegated listeners so handlers survive cart section HTML replacement.
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
const getTabsAndPanels = (drawer) => {
  const tabs = [...drawer.querySelectorAll('[role="tab"][data-cart-drawer-tab]')];
  const panels = [...drawer.querySelectorAll('[data-cart-drawer-panel]')];
  return { tabs, panels };
};

const selectTab = (drawer, index) => {
  const { tabs, panels } = getTabsAndPanels(drawer);
  if (tabs.length === 0 || tabs.length !== panels.length) return;

  tabs.forEach((tab, i) => {
    const selected = i === index;
    tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    tab.tabIndex = selected ? 0 : -1;
  });
  panels.forEach((panel, i) => {
    if (i === index) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  });
};

const initCartDrawerTabs = () => {
  const drawer = document.querySelector(CART_DRAWER_SELECTOR);
  if (!drawer) return;

  document.addEventListener('drawer:opened', (event) => {
    if (event.detail?.id !== 'cart-drawer') return;
    const openedDrawer = event.detail?.drawer;
    if (!(openedDrawer instanceof HTMLElement)) return;

    const trigger = event.detail?.trigger;
    const wishlistFirst =
      event.detail?.initialTab === 'wishlist' ||
      (trigger instanceof HTMLElement && trigger.dataset.drawerInitialTab === 'wishlist');

    selectTab(openedDrawer, wishlistFirst ? 1 : 0);
  });

  document.addEventListener('cart-drawer:select-tab', (event) => {
    const drawer = document.querySelector(CART_DRAWER_SELECTOR);
    if (!(drawer instanceof HTMLElement)) return;
    const index = Number(event.detail?.index);
    if (!Number.isFinite(index) || index < 0) return;
    selectTab(drawer, index);
  });

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-cart-drawer-tab]');
    if (!(tab instanceof HTMLElement)) return;
    const tabDrawer = tab.closest(CART_DRAWER_SELECTOR);
    if (!tabDrawer) return;
    const { tabs } = getTabsAndPanels(tabDrawer);
    const index = tabs.indexOf(tab);
    if (index < 0) return;
    selectTab(tabDrawer, index);
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-cart-drawer-tab]');
    if (!(tab instanceof HTMLElement)) return;
    const tabDrawer = tab.closest(CART_DRAWER_SELECTOR);
    if (!tabDrawer) return;
    const { tabs } = getTabsAndPanels(tabDrawer);
    const index = tabs.indexOf(tab);
    if (index < 0) return;

    let nextIndex = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = tabs.length - 1;
    }

    if (nextIndex !== null) {
      selectTab(tabDrawer, nextIndex);
      const { tabs: nextTabs } = getTabsAndPanels(tabDrawer);
      const nextTab = nextTabs[nextIndex];
      if (nextTab instanceof HTMLElement) nextTab.focus();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerTabs);
} else {
  initCartDrawerTabs();
}
